import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import DealerLayout from '../components/DealerLayout';
import api from '../api/axios';
import { C } from '../theme';
import { useAuth } from '../context/AuthContext';

// ── Inline searchable dropdown (no absolute positioning) ─────────────────────
function SearchDropdown({ options, value, onChange, placeholder, getLabel, getMeta, disabled }) {
  const [search, setSearch] = useState('');
  const [open,   setOpen]   = useState(false);

  const selected = value != null ? options.find(o => o.id === value) : null;

  const filtered = options.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const label = getLabel(o).toLowerCase();
    const meta  = getMeta ? getMeta(o).toLowerCase() : '';
    return label.includes(q) || meta.includes(q);
  });

  if (selected && !open) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '9px 12px', border: `1.5px solid ${C.primary}`,
        borderRadius: '8px', background: C.primaryLight,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: C.primaryDeep, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getLabel(selected)}
          </div>
          {getMeta && (
            <div style={{ fontSize: '11px', color: C.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getMeta(selected)}
            </div>
          )}
        </div>
        {!disabled && <>
          <button
            type="button"
            onClick={() => { setSearch(''); setOpen(true); }}
            style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '5px', background: `${C.primary}20`, color: C.primaryDeep, border: `1px solid ${C.primary}40`, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
          >Change</button>
          <button
            type="button"
            onClick={() => onChange(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSub, fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}
          >✕</button>
        </>}
      </div>
    );
  }

  return (
    <div>
      <input
        autoFocus={open}
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder={placeholder || 'Search…'}
        disabled={disabled}
        style={{ ...inputBase, borderColor: C.border }}
      />
      {open && (
        <div style={{
          marginTop: '4px', border: `1.5px solid ${C.border}`, borderRadius: '8px',
          maxHeight: '220px', overflowY: 'auto', background: C.white,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '14px', textAlign: 'center', color: C.textSub, fontSize: '12px' }}>
              {options.length === 0 ? 'No options available' : 'No results found'}
            </div>
          ) : filtered.slice(0, 60).map(o => (
            <div
              key={o.id}
              onMouseDown={() => { onChange(o.id); setSearch(''); setOpen(false); }}
              style={{
                padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`,
                background: value === o.id ? C.primaryLight : 'transparent',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (value !== o.id) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { if (value !== o.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{getLabel(o)}</div>
              {getMeta && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{getMeta(o)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const BLANK = {
  dealer_id:          null,
  client_id:          null,
  catSel:             {},   // { [category_name]: item_id } — one per category
  application_id:     null,
  application_number: '',
  amount:             '',
  amc_amount:         '',
  migration_item_id:  null,
  notes:              '',
};

export default function DealerMapping() {
  const { user } = useAuth();
  const isDealer = user?.role === 'dealer';

  const [mappings,      setMappings]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [dealers,       setDealers]       = useState([]);
  const [clients,       setClients]       = useState([]);
  const [applications,  setApplications]  = useState([]);
  const [availItems,    setAvailItems]    = useState([]);
  const [inStockItems,  setInStockItems]  = useState([]);
  const [loadingItems,  setLoadingItems]  = useState(false);
  const [myDealerId,    setMyDealerId]    = useState(null);

  const [modal,      setModal]      = useState(null);
  const [selMap,     setSelMap]     = useState(null);
  const [form,       setForm]       = useState(BLANK);
  const [saving,     setSaving]     = useState(false);
  const [errs,       setErrs]       = useState({});
  const [toast,      setToast]      = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [migrationOn,       setMigrationOn]       = useState(false);
  const [loadingMigItems,   setLoadingMigItems]   = useState(false);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadMappings = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/dealer-mapping'); setMappings(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadMappings();
    api.get('/api/applications').then(r => setApplications(r.data)).catch(() => {});
    if (!isDealer) {
      api.get('/api/dealers').then(r => setDealers(r.data)).catch(() => {});
    } else {
      api.get('/api/dealer/me').then(r => setMyDealerId(r.data.id)).catch(() => {});
    }
  }, [isDealer, loadMappings]);

  // Load clients when dealer changes in form
  useEffect(() => {
    const did = form.dealer_id || myDealerId;
    if (!did) { setClients([]); return; }
    api.get(`/api/clients?dealer_id=${did}`).then(r => setClients(r.data)).catch(() => setClients([]));
  }, [form.dealer_id, myDealerId]);

  // Load available items when modal opens / dealer changes
  useEffect(() => {
    if (!modal) return;
    const did = form.dealer_id || myDealerId;
    if (!did) { setAvailItems([]); return; }
    setLoadingItems(true);
    api.get(`/api/dealer-mapping/available-items?dealer_id=${did}`)
      .then(r => {
        if (modal === 'edit' && selMap) {
          const alreadyIds = new Set(selMap.items.map(it => it.inventory_item_id));
          const existing = selMap.items.map(it => ({
            id: it.inventory_item_id, item_id: it.item_id,
            serial_number: it.serial_number, ref_number: it.ref_number,
            category_name: it.category_name, type_name: it.type_name,
            amount: 0, amc_amount: 0,
          }));
          setAvailItems([...existing, ...r.data.filter(x => !alreadyIds.has(x.id))]);
        } else {
          setAvailItems(r.data);
        }
      })
      .catch(() => setAvailItems([]))
      .finally(() => setLoadingItems(false));
  }, [form.dealer_id, myDealerId, modal]); // selMap omitted intentionally

  // Load in-stock items when migration checkbox is toggled on
  useEffect(() => {
    if (!migrationOn) return;
    setLoadingMigItems(true);
    api.get('/api/inventory-items/in-stock')
      .then(r => {
        // Merge fresh in-stock list with any pre-seeded existing migration device
        setInStockItems(prev => {
          const freshIds = new Set(r.data.map(x => x.id));
          const kept = prev.filter(x => !freshIds.has(x.id)); // pre-seeded items not in fresh list
          return [...kept, ...r.data];
        });
      })
      .catch(() => {}) // keep the pre-seeded item on error
      .finally(() => setLoadingMigItems(false));
  }, [migrationOn]);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const openAdd = () => {
    setForm({ ...BLANK, dealer_id: myDealerId || null });
    setErrs({}); setSelMap(null); setItemSearch(''); setMigrationOn(false); setModal('add');
  };

  const openEdit = (m) => {
    // Build catSel from existing items grouped by category
    const catSel = {};
    m.items.forEach(it => { if (it.category_name) catSel[it.category_name] = it.inventory_item_id; });
    setForm({
      dealer_id:          m.dealer_id,
      client_id:          m.client_id,
      catSel,
      application_id:     m.application_id     || null,
      application_number: m.application_number || '',
      amount:             m.amount     ?? '',
      amc_amount:         m.amc_amount ?? '',
      migration_item_id:  m.migration_item_id  || null,
      notes:              m.notes      || '',
    });
    // Pre-seed migration item so the dropdown can display the already-saved device
    // (its status may no longer be in-stock, so the API won't return it)
    if (m.migration_item) {
      setInStockItems([m.migration_item]);
    } else {
      setInStockItems([]);
    }
    setMigrationOn(!!m.migration_item_id);
    setErrs({}); setSelMap(m); setItemSearch(''); setModal('edit');
  };

  const openDelete = (m) => { setSelMap(m); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelMap(null); setErrs({}); setAvailItems([]); setInStockItems([]); setMigrationOn(false); };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const selectForCategory = (item) => {
    const cat = item.category_name;
    setForm(p => {
      // toggle off if same item clicked again
      const nextCatSel = { ...p.catSel };
      if (nextCatSel[cat] === item.id) {
        delete nextCatSel[cat];
      } else {
        nextCatSel[cat] = item.id;
      }
      // recalculate totals from all selected items across categories
      const selIds = new Set(Object.values(nextCatSel));
      const selItems = availItems.filter(it => selIds.has(it.id));
      const totalAmt = selItems.reduce((s, it) => s + (it.amount || 0), 0);
      const totalAmc = selItems.reduce((s, it) => s + (it.amc_amount || 0), 0);
      return {
        ...p,
        catSel:     nextCatSel,
        amount:     totalAmt > 0 ? totalAmt : p.amount,
        amc_amount: totalAmc > 0 ? totalAmc : p.amc_amount,
      };
    });
  };

  const save = async () => {
    const e = {};
    const did = isDealer ? myDealerId : form.dealer_id;
    if (!did)                          e.dealer_id          = 'Select a dealer.';
    if (!form.client_id)               e.client_id          = 'Select a client.';
    if (!form.application_id)          e.application_id     = 'Select an application.';
    if (!form.application_number.trim()) e.application_number = 'Application number is required.';
    if (Object.keys(e).length) return setErrs(e);

    setSaving(true);
    try {
      const body = {
        dealer_id:          isDealer ? myDealerId : Number(form.dealer_id),
        client_id:          Number(form.client_id),
        inventory_item_ids: Object.values(form.catSel).filter(Boolean),
        application_id:     form.application_id     || null,
        application_number: form.application_number.trim(),
        amount:             Number(form.amount)     || 0,
        amc_amount:         Number(form.amc_amount) || 0,
        migration_item_id:  migrationOn ? form.migration_item_id : null,
        notes:              form.notes || null,
      };
      if (selMap) await api.put(`/api/dealer-mapping/${selMap.id}`, body);
      else        await api.post('/api/dealer-mapping', body);
      await loadMappings();
      showToast('success', selMap ? 'Mapping updated.' : 'Mapping created.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/dealer-mapping/${selMap.id}`);
      await loadMappings();
      showToast('success', 'Mapping deleted.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  // ── Item picker helpers ───────────────────────────────────────────────────
  const q = itemSearch.toLowerCase();
  const filteredItems = availItems.filter(it =>
    !q ||
    it.serial_number?.toLowerCase().includes(q) ||
    it.ref_number?.toLowerCase().includes(q) ||
    it.category_name?.toLowerCase().includes(q) ||
    it.type_name?.toLowerCase().includes(q) ||
    it.item_id?.toLowerCase().includes(q)
  );
  const groupedItems = filteredItems.reduce((acc, it) => {
    const cat = it.category_name || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(it);
    return acc;
  }, {});

  const fmtAmt = n => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const LayoutComp = isDealer ? DealerLayout : Layout;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <LayoutComp title="Dealer Mapping">

      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px' }}>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total',    val: mappings.length,                                      dot: C.primary },
            { label: 'Active',   val: mappings.filter(m => m.status === 'active').length,   dot: C.success },
            { label: 'Inactive', val: mappings.filter(m => m.status === 'inactive').length, dot: C.danger  },
          ].map(({ label, val, dot }) => (
            <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
              <div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: C.textMain }}>{val}</div>
                <div style={{ fontSize: '11px', color: C.textSub }}>{label}</div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={openAdd} style={btnPrimary}>+ Add Mapping</button>
      </div>

      {/* Table */}
      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {[
                  'Mapping ID',
                  ...(isDealer ? [] : ['Dealer']),
                  'Client',
                  'Items',
                  'Application',
                  'App Number',
                  'Amount',
                  'AMC',
                  'Migration',
                  'Status',
                  'Date',
                  'Actions',
                ].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={99} style={emptyCell}>Loading…</td></tr>
              ) : mappings.length === 0 ? (
                <tr><td colSpan={99} style={emptyCell}>No mappings yet. Click "+ Add Mapping" to start.</td></tr>
              ) : mappings.map((m, i) => (
                <tr key={m.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                  <td style={td}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.primaryDeep }}>{m.mapping_id}</span>
                  </td>
                  {!isDealer && (
                    <td style={td}>
                      <span style={{ background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                        🤝 {m.dealer_name}
                      </span>
                    </td>
                  )}
                  <td style={td}>
                    <div style={{ fontWeight: 700, color: C.textMain, fontSize: '13px' }}>{m.client_name}</div>
                    <div style={{ fontSize: '11px', color: C.primaryDeep, fontFamily: 'monospace' }}>{m.client_display_id}</div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {m.items.length === 0 ? (
                        <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                      ) : m.items.map(it => (
                        <div key={it.id} style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '11px' }}>
                          <span style={{ color: '#1D4ED8', fontWeight: 600 }}>{it.category_name}</span>
                          {it.serial_number && <span style={{ color: '#6B7280', marginLeft: '4px' }}>· {it.serial_number}</span>}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ fontSize: '13px', color: C.textMain, fontWeight: 600 }}>{m.application_label || '—'}</div>
                    {m.application_reg_number && <div style={{ fontSize: '11px', color: C.textSub }}>{m.application_reg_number}</div>}
                  </td>
                  <td style={td}><span style={{ fontFamily: 'monospace', fontSize: '12px', color: C.textMain, fontWeight: 600 }}>{m.application_number || '—'}</span></td>
                  <td style={td}><span style={{ fontWeight: 700, color: C.success, fontSize: '13px' }}>{fmtAmt(m.amount)}</span></td>
                  <td style={td}><span style={{ fontWeight: 700, color: '#7C3AED', fontSize: '13px' }}>{fmtAmt(m.amc_amount)}</span></td>
                  <td style={td}>
                    {m.migration_item ? (
                      <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '6px', padding: '3px 8px', fontSize: '11px' }}>
                        <div style={{ color: '#C2410C', fontWeight: 700 }}>🔄 {m.migration_item.category_name}</div>
                        {m.migration_item.serial_number && <div style={{ color: '#9A3412', marginTop: '1px' }}>{m.migration_item.serial_number}</div>}
                      </div>
                    ) : <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>}
                  </td>
                  <td style={td}>
                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: m.status === 'active' ? C.successBg : C.dangerBg, color: m.status === 'active' ? C.successText : C.dangerText }}>
                      {m.status === 'active' ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td style={td}><span style={{ fontSize: '12px', color: C.textSub }}>{m.created_at || '—'}</span></td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <SmBtn label="Edit"   color="#3B82F6" onClick={() => openEdit(m)} />
                      <SmBtn label="Delete" color={C.danger} onClick={() => openDelete(m)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {(modal === 'add' || modal === 'edit') && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '680px' }}>
            <MHeader
              title={modal === 'add' ? 'Add Mapping' : `Edit — ${selMap?.mapping_id}`}
              onClose={closeModal}
            />
            <div style={{ padding: '24px 28px', overflowY: 'auto', maxHeight: '78vh' }}>

              {/* Dealer — admin only */}
              {!isDealer && (
                <FF label="Dealer *" error={errs.dealer_id}>
                  <SearchDropdown
                    options={dealers}
                    value={form.dealer_id}
                    onChange={v => { setForm(p => ({ ...p, dealer_id: v, client_id: null, inventory_item_ids: [] })); setErrs(p => ({ ...p, dealer_id: '' })); }}
                    placeholder="Search dealer by name or ID…"
                    getLabel={d => d.company_name}
                    getMeta={d => `${d.dealer_id} · ${d.contact_number || ''}`}
                    disabled={modal === 'edit'}
                  />
                </FF>
              )}

              {/* Client */}
              <FF label="Client *" error={errs.client_id}>
                <SearchDropdown
                  options={clients}
                  value={form.client_id}
                  onChange={v => { setForm(p => ({ ...p, client_id: v })); setErrs(p => ({ ...p, client_id: '' })); }}
                  placeholder={
                    (!form.dealer_id && !myDealerId)
                      ? 'Select a dealer first…'
                      : clients.length === 0
                        ? 'No clients for this dealer'
                        : 'Search client by name, ID, contact…'
                  }
                  getLabel={c => c.client_name}
                  getMeta={c => `${c.client_id}${c.contact ? ' · ' + c.contact : ''}`}
                  disabled={!form.dealer_id && !myDealerId}
                />
              </FF>

              {/* Inventory items picker */}
              <div style={{ marginBottom: '18px' }}>
                <label style={labelSt}>Inventory Items</label>
                <div style={{ marginTop: '8px', border: `1.5px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, background: '#F9FAFB' }}>
                    <input
                      placeholder="Search by serial, ref, category, type…"
                      value={itemSearch}
                      onChange={e => setItemSearch(e.target.value)}
                      style={{ width: '100%', border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', color: C.textMain }}
                    />
                  </div>
                  <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '8px' }}>
                    {loadingItems ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: C.textSub, fontSize: '13px' }}>Loading items…</div>
                    ) : !form.dealer_id && !myDealerId ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: C.textSub, fontSize: '12px' }}>Select a dealer first</div>
                    ) : availItems.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: C.textSub, fontSize: '12px' }}>No unmapped inventory items available</div>
                    ) : filteredItems.length === 0 ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: C.textSub, fontSize: '12px' }}>No items match your search</div>
                    ) : Object.entries(groupedItems).map(([cat, items]) => (
                      <div key={cat} style={{ marginBottom: '10px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 6px' }}>{cat}</div>
                        {items.map(item => {
                          const selected = form.catSel[cat] === item.id;
                          return (
                            <div
                              key={item.id}
                              onClick={() => selectForCategory(item)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '9px 10px', borderRadius: '8px', cursor: 'pointer',
                                background: selected ? C.primaryLight : 'transparent',
                                border: `1.5px solid ${selected ? C.primary : 'transparent'}`,
                                marginBottom: '4px', transition: 'all 0.12s',
                              }}
                            >
                              {/* Radio dot */}
                              <div style={{ width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, border: `2px solid ${selected ? C.primary : C.border}`, background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {selected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.primary }} />}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: C.textMain }}>{item.type_name || item.category_name}</div>
                                <div style={{ fontSize: '11px', color: C.textSub, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                  {item.serial_number && <span>SN: {item.serial_number}</span>}
                                  {item.ref_number    && <span>Ref: {item.ref_number}</span>}
                                  {item.item_id       && <span style={{ fontFamily: 'monospace', color: C.primaryDeep }}>{item.item_id}</span>}
                                </div>
                              </div>
                              {(item.amount > 0 || item.amc_amount > 0) && (
                                <div style={{ textAlign: 'right', fontSize: '11px', flexShrink: 0 }}>
                                  {item.amount > 0     && <div style={{ color: C.success,  fontWeight: 600 }}>{fmtAmt(item.amount)}</div>}
                                  {item.amc_amount > 0 && <div style={{ color: '#7C3AED', fontWeight: 600 }}>AMC {fmtAmt(item.amc_amount)}</div>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  {Object.keys(form.catSel).length > 0 && (
                    <div style={{ padding: '8px 12px', borderTop: `1px solid ${C.border}`, background: C.primaryLight, fontSize: '12px', color: C.primaryDeep, fontWeight: 600 }}>
                      ✓ {Object.keys(form.catSel).length} item{Object.keys(form.catSel).length > 1 ? 's' : ''} selected
                      {' '}({Object.keys(form.catSel).join(', ')})
                    </div>
                  )}
                </div>
              </div>

              {/* Application + Number */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <FF label="Application *" error={errs.application_id}>
                  <SearchDropdown
                    options={applications.filter(a => a.status === 'active')}
                    value={form.application_id}
                    onChange={v => {
                      const app = applications.find(a => a.id === v);
                      setForm(p => ({
                        ...p,
                        application_id: v,
                        // auto-fill app number from registration_number if field is empty
                        application_number: !p.application_number && app?.registration_number
                          ? app.registration_number
                          : p.application_number,
                      }));
                      setErrs(p => ({ ...p, application_id: '' }));
                    }}
                    placeholder="Search by make, model, vehicle type…"
                    getLabel={a => [a.vehicle_type, a.make, a.model].filter(Boolean).join(' · ')}
                    getMeta={a => [a.app_id, a.registration_number, a.year].filter(Boolean).join('  ·  ')}
                  />
                </FF>
                <FF label="Application Number *" error={errs.application_number}>
                  <input
                    value={form.application_number}
                    onChange={e => { f('application_number', e.target.value); setErrs(p => ({ ...p, application_number: '' })); }}
                    placeholder="e.g. MH12AB1234"
                    style={{ ...inputBase, borderColor: errs.application_number ? C.danger : C.border }}
                  />
                </FF>
              </div>

              {/* Amount + AMC */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <FF label="Amount (₹)">
                  <input type="number" min="0" step="0.01" value={form.amount}
                    onChange={e => f('amount', e.target.value)} placeholder="0.00"
                    style={{ ...inputBase, borderColor: C.border }} />
                </FF>
                <FF label="AMC Amount (₹)">
                  <input type="number" min="0" step="0.01" value={form.amc_amount}
                    onChange={e => f('amc_amount', e.target.value)} placeholder="0.00"
                    style={{ ...inputBase, borderColor: C.border }} />
                </FF>
              </div>

              {/* Migration Device checkbox */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                  <div
                    onClick={() => { setMigrationOn(p => !p); if (!migrationOn) f('migration_item_id', null); }}
                    style={{
                      width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                      border: `2px solid ${migrationOn ? '#EA580C' : C.border}`,
                      background: migrationOn ? '#EA580C' : C.white,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {migrationOn && <span style={{ color: '#fff', fontSize: '13px', lineHeight: 1, fontWeight: 700 }}>✓</span>}
                  </div>
                  <div onClick={() => { setMigrationOn(p => !p); if (!migrationOn) f('migration_item_id', null); }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: migrationOn ? '#C2410C' : C.textMain }}>🔄 Migration Device</div>
                    <div style={{ fontSize: '11px', color: C.textSub }}>Map an existing in-stock device being migrated</div>
                  </div>
                </label>
              </div>

              {/* Migration device search — appears when checkbox checked */}
              {migrationOn && (
                <div style={{ marginBottom: '18px', padding: '16px', background: '#FFF7ED', border: '1.5px solid #FED7AA', borderRadius: '10px' }}>
                  <label style={{ ...labelSt, color: '#C2410C', marginBottom: '8px', display: 'block' }}>
                    🔄 Select Migration Device (In-Stock)
                  </label>
                  {loadingMigItems ? (
                    <div style={{ padding: '10px 0', fontSize: '12px', color: '#9A3412' }}>Loading in-stock items…</div>
                  ) : inStockItems.length === 0 ? (
                    <div style={{ padding: '10px 0', fontSize: '12px', color: '#9A3412' }}>No in-stock items available</div>
                  ) : (
                    <SearchDropdown
                      options={inStockItems}
                      value={form.migration_item_id}
                      onChange={v => f('migration_item_id', v)}
                      placeholder="Search by serial, ref, category, type…"
                      getLabel={it => [it.category_name, it.type_name].filter(Boolean).join(' · ') || it.item_id || `Item ${it.id}`}
                      getMeta={it => [
                        it.serial_number ? `SN: ${it.serial_number}` : '',
                        it.ref_number    ? `Ref: ${it.ref_number}`   : '',
                        it.item_id       ? it.item_id                : '',
                      ].filter(Boolean).join('  ·  ')}
                    />
                  )}
                </div>
              )}

              {/* Status — edit only */}
              {modal === 'edit' && (
                <FF label="Status">
                  <select value={form.status || 'active'} onChange={e => f('status', e.target.value)} style={{ ...inputBase, borderColor: C.border }}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </FF>
              )}

              {/* Notes */}
              <FF label="Notes">
                <textarea value={form.notes} onChange={e => f('notes', e.target.value)}
                  placeholder="Optional notes…" rows={2}
                  style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              </FF>
            </div>
            <MFooter onClose={closeModal} onSave={save} saving={saving}
              label={modal === 'add' ? '+ Create Mapping' : '💾 Update Mapping'} />
          </div>
        </Overlay>
      )}

      {/* Delete modal */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <MHeader title="Delete Mapping" onClose={closeModal} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete mapping <strong style={{ color: C.danger }}>{selMap?.mapping_id}</strong> for client <strong>{selMap?.client_name}</strong>?
                <br /><span style={{ fontSize: '12px', color: C.textSub }}>This cannot be undone.</span>
              </p>
            </div>
            <MFooter onClose={closeModal} onSave={confirmDelete} saving={saving} label="Delete" danger />
          </div>
        </Overlay>
      )}
    </LayoutComp>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer' }}>
      {label}
    </button>
  );
}

function FF({ label, error, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={labelSt}>{label}</label>
      <div style={{ marginTop: '6px' }}>{children}</div>
      {error && <div style={{ fontSize: '11px', color: C.danger, marginTop: '4px' }}>⚠ {error}</div>}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      {children}
    </div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{ padding: '22px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function MFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{ padding: '16px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const labelSt    = { display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain };
const inputBase  = { width: '100%', padding: '9px 12px', border: '1.5px solid', borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', boxSizing: 'border-box', background: C.white };
const card       = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox   = { background: C.white, borderRadius: '16px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th         = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td         = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell  = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const btnPrimary = { padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)` };
const btnGhost   = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnDanger  = { padding: '10px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
